import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Heart, Coffee, Droplet, Shield } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const FirstTimerZonePage = () => {
  const { getContent } = useSiteContent();

  const tips = [
    { icon: <Coffee size={32} />, title: getContent('first_timer_tip1_title', 'Eat Well'), description: getContent('first_timer_tip1_text', 'Have a full meal before') },
    { icon: <Droplet size={32} />, title: getContent('first_timer_tip2_title', 'Stay Hydrated'), description: getContent('first_timer_tip2_text', 'Drink plenty of water') },
    { icon: <Heart size={32} />, title: getContent('first_timer_tip3_title', 'Bring Support'), description: getContent('first_timer_tip3_text', 'Friends are welcome') },
    { icon: <Shield size={32} />, title: getContent('first_timer_tip4_title', 'Trust the Process'), description: getContent('first_timer_tip4_text', "We've got you") },
  ];

  const faqs = [
    { question: getContent('first_timer_faq1_q', 'Does it hurt?'), answer: getContent('first_timer_faq1_a', '') },
    { question: getContent('first_timer_faq2_q', 'How do I prepare?'), answer: getContent('first_timer_faq2_a', '') },
    { question: getContent('first_timer_faq3_q', 'How long does healing take?'), answer: getContent('first_timer_faq3_a', '') },
    { question: getContent('first_timer_faq4_q', 'Can I bring a friend?'), answer: getContent('first_timer_faq4_a', '') },
  ];

  return (
    <div data-testid="first-timer-zone-page" className="tattoo-bg-functional min-h-screen">
      <Header />

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20">

        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-7xl md:text-8xl font-bold mb-6 leading-none whitespace-pre-line">
              {getContent('first_timer_title', 'YOUR FIRST\nTATTOO?')}
            </h1>
            <p className="font-heading text-3xl md:text-4xl mb-8">
              {getContent('first_timer_subtitle', "WE'VE GOT YOU.")}
            </p>
            <p className="font-body text-xl max-w-2xl leading-relaxed">
              {getContent('first_timer_intro', "Getting your first tattoo is a big step. We're here to make it comfortable, safe, and memorable.")}
            </p>
          </motion.div>
        </section>

        <section className="mb-20">
          <div className="border-t-2 border-black pt-8 mb-12">
            <h2 className="font-heading text-5xl font-bold">
              {getContent('first_timer_prep_title', 'HOW TO PREPARE')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="paper-texture p-8 text-center"
                data-testid={`tip-${index + 1}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-black text-white mb-4">
                  {tip.icon}
                </div>
                <h3 className="font-heading text-2xl font-bold mb-2">{tip.title}</h3>
                <p className="font-body text-base text-gray-600">{tip.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="mb-20">
          <div className="border-t-2 border-black pt-8 mb-12">
            <h2 className="font-heading text-5xl font-bold">
              {getContent('first_timer_faq_title', 'COMMON QUESTIONS')}
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="paper-texture p-8"
                data-testid={`faq-${index + 1}`}
              >
                <h3 className="font-heading text-2xl font-bold mb-4">{faq.question}</h3>
                <p className="font-body text-lg leading-relaxed text-gray-700 whitespace-pre-line">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="paper-texture p-12 text-center"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              {getContent('first_timer_cta_title', "YOU'RE IN SAFE HANDS")}
            </h2>
            <p className="font-body text-lg leading-relaxed mb-8 max-w-2xl mx-auto whitespace-pre-line">
              {getContent('first_timer_cta_text', 'Our studio is sterile, our artist is experienced, and our priority is your comfort.')}
            </p>
            <a
              href="/quote-estimator"
              data-testid="start-your-journey-button"
              className="inline-block bg-black text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
            >
              {getContent('first_timer_cta_button', 'START YOUR JOURNEY →')}
            </a>
          </motion.div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default FirstTimerZonePage;
